import type { TableColumnsType } from 'antd';
import { Alert, Empty, InputNumber, Space, Table, Tag } from 'antd';
import {
  formatPublicMoney,
  type PublicShareModel,
} from '../../../quote-integration/publicShareModel';
import { HoverImagePreview } from '../../../shared/HoverImagePreview';
import { formatMoney } from '../mock/catalog';
import { isDeviceAssetReference } from '../state/deviceMedia';
import {
  assertFrozenQuotationProjectionPair,
  frozenQuotationHeaderProjectionIssue,
  frozenQuotationListProjectionIssue,
} from '../state/frozenQuotationProjection';
import type {
  FrozenQuotationCell,
  FrozenQuotationFieldDescriptor,
  FrozenQuotationHeaderProjection,
  FrozenQuotationListProjection,
  FrozenQuotationListRow,
} from '../state/types';
import styles from '../styles.module.css';

export interface FrozenQuotationListCellChange {
  rowId: string;
  fieldId: string;
  value: number;
}

function formatFrozenValue(
  field: FrozenQuotationFieldDescriptor,
  cell: FrozenQuotationCell,
) {
  if (cell.value === null || cell.value === '') return '-';
  if (field.valueType === 'money' && typeof cell.value === 'number')
    return formatMoney(cell.value);
  if (field.valueType === 'percent' && typeof cell.value === 'number')
    return `${cell.value}%`;
  if (field.valueType === 'boolean' && typeof cell.value === 'boolean')
    return cell.value ? '是' : '否';
  if (Array.isArray(cell.value)) return cell.value.join('；') || '-';
  return String(cell.value);
}

function FrozenImageValue({
  value,
  label,
}: {
  value: FrozenQuotationCell['value'];
  label: string;
}) {
  const source =
    typeof value === 'string' && value.trim() && !isDeviceAssetReference(value)
      ? value
      : undefined;
  if (!source) {
    return (
      <span role="img" aria-label={`${label} 图片不可用`}>
        图片不可用
      </span>
    );
  }
  return (
    <HoverImagePreview
      sources={[source]}
      alt={`${label} 图片`}
      frameClassName={styles.productImage}
    />
  );
}

export function FrozenQuotationListRenderer({
  projection,
  editable = false,
  onCellChange,
  emptyText = '冻结报价清单无设备',
  publicShare,
}: {
  projection?: FrozenQuotationListProjection;
  publicShare?: PublicShareModel;
  editable?: boolean;
  onCellChange?: (change: FrozenQuotationListCellChange) => void;
  emptyText?: string;
}) {
  const issue = publicShare
    ? undefined
    : frozenQuotationListProjectionIssue(projection);
  if (issue) {
    return (
      <Alert
        type="error"
        showIcon
        title="冻结报价清单不可用"
        description={issue}
      />
    );
  }
  // Public DTO is already clipped/validated at the port boundary. No fabricated
  // legacy policy snapshot, discount or second pricing model is reconstructed.
  const publicFields = publicShare
    ? [
        { fieldId: 'name', label: '清单名称', valueType: 'text' as const },
        { fieldId: 'quantity', label: '数量', valueType: 'number' as const },
        ...(publicShare.pricingVisibility === 'visible'
          ? [
              {
                fieldId: 'unitPriceCents',
                label: `${publicShare.pricingView === 'rental' ? '租赁' : '购买'}单价`,
                valueType: 'money' as const,
              },
              {
                fieldId: 'lineTotalCents',
                label: '行金额',
                valueType: 'money' as const,
              },
            ]
          : []),
      ]
    : [];
  const publicRows: FrozenQuotationListRow[] =
    publicShare?.items.map((item) => ({
      rowId: item.itemId,
      rowType: 'quote-line',
      values: {
        name: { valueType: 'text', value: item.name },
        quantity: { valueType: 'number', value: item.quantity },
        ...(publicShare.pricingVisibility === 'visible'
          ? {
              unitPriceCents: {
                valueType: 'money' as const,
                value: item.unitPriceCents ?? null,
              },
              lineTotalCents: {
                valueType: 'money' as const,
                value: item.lineTotalCents ?? null,
              },
            }
          : {}),
      },
    })) ?? [];
  const columns: TableColumnsType<FrozenQuotationListRow> = publicShare
    ? publicFields.map((field) => ({
        title: <span data-frozen-field-id={field.fieldId}>{field.label}</span>,
        dataIndex: ['values', field.fieldId],
        key: field.fieldId,
        width: 132,
        render: (cell: FrozenQuotationCell) =>
          field.valueType === 'money' && typeof cell.value === 'number'
            ? formatPublicMoney(cell.value)
            : String(cell.value ?? '—'),
      }))
    : (projection?.fields ?? []).map((field) => ({
        title: <span data-frozen-field-id={field.fieldId}>{field.label}</span>,
        dataIndex: ['values', field.fieldId],
        key: field.fieldId,
        width: field.width === 'long' ? 180 : 132,
        render: (value: FrozenQuotationCell, row) => {
          if (field.presentation === 'image')
            return <FrozenImageValue value={value.value} label={field.label} />;
          if (
            field.customerEdit === 'quantity' &&
            onCellChange &&
            typeof value.value === 'number'
          ) {
            return (
              <InputNumber
                min={1}
                precision={0}
                disabled={!editable}
                value={value.value}
                onChange={(next) => {
                  if (typeof next !== 'number') return;
                  onCellChange({
                    rowId: row.rowId,
                    fieldId: field.fieldId,
                    value: next,
                  });
                }}
              />
            );
          }
          return formatFrozenValue(field, value);
        },
      }));
  return (
    <Table<FrozenQuotationListRow>
      data-frozen-quotation-list="v1"
      rowKey="rowId"
      size="small"
      pagination={false}
      dataSource={publicShare ? publicRows : projection?.rows}
      columns={columns}
      scroll={{ x: Math.max(480, columns.length * 132) }}
      locale={{ emptyText: <Empty description={emptyText} /> }}
    />
  );
}

/** Shared typed header rendering; it never contributes columns to the list. */
export function FrozenQuotationHeaderSummary({
  listProjection,
  headerProjection,
}: {
  listProjection: FrozenQuotationListProjection;
  headerProjection: FrozenQuotationHeaderProjection;
}) {
  const listIssue = frozenQuotationListProjectionIssue(listProjection);
  const headerIssue = frozenQuotationHeaderProjectionIssue(
    headerProjection,
    listProjection.combinationSnapshot,
  );
  try {
    if (listIssue) throw new Error(listIssue);
    if (headerIssue) throw new Error(headerIssue);
    assertFrozenQuotationProjectionPair(listProjection, headerProjection);
  } catch (error) {
    return (
      <Alert
        type="error"
        showIcon
        title="冻结报价抬头不可用"
        description={
          error instanceof Error ? error.message : '冻结报价抬头校验失败'
        }
      />
    );
  }
  return (
    <Space wrap data-frozen-quotation-header="v1">
      <Tag color="blue">客户：{headerProjection.customerSnapshot.name}</Tag>
      <Tag>联系电话：{headerProjection.customerSnapshot.phone}</Tag>
      <Tag>有效期：{headerProjection.validUntilSnapshot}</Tag>
      <strong>{formatMoney(headerProjection.totalSnapshot)}</strong>
    </Space>
  );
}
